import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { SlArrowDown } from "react-icons/sl";
import Shop from "./Shop";
import { db } from "../firebaseConfig";
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import ProductCard from "./ProductCard";
import { FAQSection } from "./FAQ";
import CountdownTimer from "./CountdownTimer";
import MobilePromoPopup from "./MobilePromoPopup";

// ── Hot Products Row (exported so ProductDetails can reuse it) ──────────────
const HotProductsRow = () => {
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    db.collection("Products").where("deals", "==", "hot").get()
      .then(snapshot => {
        setHotProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || hotProducts.length === 0) return null;

  return (
    <div className="w-full py-10 px-4 dark:bg-gradient-to-r from-black to-amber-900 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-bold dark:text-white text-gray-900">
            🔥 Hot Deals
          </h2>
          <Link
            to="/hot-deals"
            className="text-orange-500 hover:text-orange-600 font-semibold text-sm transition-colors"
          >
            View All →
          </Link>
        </div>
        <div
          className="flex gap-4 overflow-x-auto pb-4"
          style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {hotProducts.map((product) => (
            <div
              key={product.id}
              className="flex-shrink-0"
              style={{ width: 'calc(50vw - 24px)', maxWidth: '220px', minWidth: '150px' }}
            >
              <ProductCard data={product} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { HotProductsRow };

const brandImages = ['/hero1.png', '/hero2.png', '/hero3.png'];
const promoImage = '/dis-ban.png';
const secondCarouselImages = [promoImage, '/m1.png', '/m2.png', '/m3.png', '/m4.png'];
const secondCarouselWriteups = [
  'Launch Week — 20% OFF! June 23–29.',
  'We are here to serve you better.',
  "We deliver nationwide. Trust us today and let's make your journey an easy one.",
  'Distance is never a barrier. Veth Interior have you covered.',
  "Happy customer's experience!",
];

const Hero = () => {
  const navigate = useNavigate();

  const handleShopNow = () => {
    navigate("/shop");
    window.scrollTo(0, 0);
  };

  const BrandingOverlay = ({ mobile = false }) => (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center z-[5] px-6 text-center pointer-events-none pt-24"
    >
      <div className="pointer-events-auto flex flex-col items-center">
        <p
          className={`font-bold text-white tracking-wide drop-shadow-[0_0_30px_rgba(0,0,0,0.95)] ${mobile ? 'text-3xl' : 'text-6xl lg:text-7xl'
            }`}
        >
          VETH INTERIORS
        </p>
        <p
          className={`font-semibold text-amber-400 drop-shadow-[0_0_20px_rgba(0,0,0,0.9)] mt-1 ${mobile ? 'text-xl' : 'text-2xl lg:text-3xl'
            }`}
        >
          Transform Your Space into Luxury
        </p>

        <button
          onClick={handleShopNow}
          className={`bg-gradient-to-r from-amber-600 to-yellow-600 mt-10 text-white font-bold rounded-full shadow-2xl hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 ${mobile
            ? 'px-8 py-3 text-base animate-pulse mb-10 '
            : 'px-12 py-4 text-lg animate-bounce mb-8'
            }`}
        >
          Shop Now
        </button>

        <CountdownTimer compact={mobile} className="mb-6" />


      </div>
    </div>
  );

  const renderSlideImage = (img, isPromo = false) => (
    <div className="relative w-full h-full">
      {isPromo ? (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
          <img
            src={img}
            alt="Launch Week Promo"
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${img})` }}
        />
      )}
    </div>
  );

  return (
    <div className="dark:bg-gradient-to-r from-black to-amber-900 bg-white min-h-screen flex w-[100%] flex-col items-center text-center">
      <MobilePromoPopup />

      {/* ── DESKTOP: side-by-side ── */}
      <div className="hidden lg:flex w-full h-screen relative">
        <div className="w-[60%] h-full relative">
          <Swiper
            modules={[EffectFade, Autoplay, Pagination]}
            effect="fade"
            autoplay={{ delay: 4500, disableOnInteraction: false }}
            pagination={{ clickable: true, dynamicBullets: true }}
            loop={true}
            className="w-full h-full"
          >
            {brandImages.map((img, i) => (
              <SwiperSlide key={i}>
                <div className="relative w-full h-full">
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${img})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="w-[40%] h-full relative">
          <Swiper
            modules={[EffectFade, Autoplay, Pagination]}
            effect="fade"
            autoplay={{ delay: 6000, disableOnInteraction: false }}
            pagination={{ clickable: true, dynamicBullets: true }}
            loop={true}
            className="w-full h-full"
          >
            {secondCarouselImages.map((img, i) => (
              <SwiperSlide key={i}>
                {renderSlideImage(img, i === 0)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <BrandingOverlay />
      </div>

      {/* ── MOBILE ── */}
      <div className="lg:hidden w-full">
        <div className="w-full h-screen relative">
          <Swiper
            modules={[EffectFade, Autoplay, Pagination]}
            effect="fade"
            autoplay={{ delay: 4500, disableOnInteraction: false }}
            pagination={{ clickable: true, dynamicBullets: true }}
            loop={true}
            className="w-full h-full"
          >
            {brandImages.map((img, i) => (
              <SwiperSlide key={i}>
                <div className="relative w-full h-full">
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${img})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/75" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <BrandingOverlay mobile={true} />

          <div className="absolute bottom-6 left-0 right-0 flex justify-center z-40 pointer-events-none">
            <SlArrowDown className="text-white text-2xl animate-bounce" />
          </div>
        </div>

        <div className="w-full h-screen relative">
          <Swiper
            modules={[EffectFade, Autoplay, Pagination]}
            effect="fade"
            autoplay={{ delay: 6000, disableOnInteraction: false }}
            pagination={{ clickable: true, dynamicBullets: true }}
            loop={true}
            className="w-full h-full"
          >
            {secondCarouselImages.map((img, i) => (
              <SwiperSlide key={i}>
                <div className="relative w-full h-full">
                  <div className="absolute top-0 left-0 right-0 z-20 pt-16 px-6">
                    <p className="text-black text-xl font-bold leading-relaxed text-center">
                      {secondCarouselWriteups[i]}
                    </p>
                  </div>
                  {renderSlideImage(img, i === 0)}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <HotProductsRow />
      <Shop title={'me'} />
      <FAQSection />
    </div>
  );
};

export default Hero;
